import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';
import { MerchantType } from 'src/app/models/onboarding.model';
import { MERCHANT_TYPE_LABELS } from 'src/app/models/onboarding.labels';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { LogService } from '../../../services/log.service';

interface MerchantOption {
  id: MerchantType;
  label: string;
  emoji: string;
  description: string;
  details: string[];
}

@Component({
  selector: 'app-step-merchant-type',
  imports: [CommonModule, RouterModule, ProgressBarComponent],
  templateUrl: './step-merchant-type.component.html',
  styleUrls: ['./step-merchant-type.component.css'],
})
export class StepMerchantTypeComponent implements OnInit {
  private log = inject(LogService);
  currentStep = 1;
  totalSteps = 6;
  selectedType: MerchantType | null = null;

  merchantOptions = Object.entries(MERCHANT_TYPE_LABELS).map(
    ([id, config]) => ({
      id: id as MerchantType,
      ...config,
    }),
  );

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // 1️⃣ Charger les données depuis le backend AVANT setCurrentStep
    // (pour éviter que setCurrentStep() crée une entrée sessionStorage vide)
    this.onboardingStateService.loadFromBackend().subscribe({
      next: () => {
        // 2️⃣ Maintenant on peut définir l'étape courante
        this.onboardingStateService.setCurrentStep(1);

        // 3️⃣ Charger la sélection (locale ou restaurée depuis DB)
        const data = this.onboardingStateService.getFormData();
        if (data?.merchantType) {
          this.selectedType = data.merchantType;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.onboardingStateService.setCurrentStep(1);
        // En cas d'erreur, utiliser les données locales
        const data = this.onboardingStateService.getFormData();
        if (data?.merchantType) {
          this.selectedType = data.merchantType;
        }
        this.cdr.markForCheck();
      },
    });
  }

  selectType(typeId: MerchantType): void {
    this.selectedType = typeId;
  }

  getSelectedTypeLabel(): string {
    return (
      this.merchantOptions.find((o) => o.id === this.selectedType)?.label ?? ''
    );
  }

  continue(): void {
    if (!this.selectedType) return;

    // Sauvegarder la sélection dans le state service
    this.onboardingStateService.updateFormData({
      merchantType: this.selectedType,
    });

    // Identifier le type de marchand
    this.onboardingStateService.setMerchantType(this.selectedType);

    // 2️⃣ Sauvegarder en DB via l'API
    this.onboardingService.submitLocationType(this.selectedType).subscribe({
      next: (res) => {
        this.log.debug('Merchant type enregistré en DB :', res);
        // 3️⃣ Naviguer vers la prochaine étape
        this.router.navigate(['/onboarding/step/2']);
      },
      error: (err) => {
        this.log.error('Erreur lors de la sauvegarde du merchantType :', err);
      },
    });

    this.log.debug(`Type de localisation sélectionné: ${this.selectedType}`);
  }
}
